import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, FormControlLabel, Checkbox, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createWorkPoint, updateWorkPoint, getWorkPoint, type WorkPointListItem, type SaveWorkPointPayload } from '../../api/puncteLucru'
import { errorMessage } from '../../api/client'

const schema = z.object({
  code: z.string().min(1, 'Câmp obligatoriu').max(32),
  name: z.string().min(1, 'Câmp obligatoriu').max(128),
  address: z.string().optional(),
  city: z.string().optional(),
  isDefault: z.boolean(),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { code: '', name: '', address: '', city: '', isDefault: false }

export default function WorkPointFormDialog({ workPoint, onClose }: { workPoint: WorkPointListItem | null | 'new'; onClose: () => void }) {
  const open = workPoint !== null
  const editing = workPoint !== null && workPoint !== 'new'
  const qc = useQueryClient()

  const { data: detail } = useQuery({
    queryKey: ['administrare', 'punct-lucru', editing ? (workPoint as WorkPointListItem).id : null],
    queryFn: () => getWorkPoint((workPoint as WorkPointListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })

  useEffect(() => {
    if (editing && detail) {
      reset({ code: detail.code, name: detail.name, address: detail.address ?? '', city: detail.city ?? '', isDefault: detail.isDefault })
    } else if (workPoint === 'new') {
      reset(EMPTY)
    }
  }, [workPoint, editing, detail, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload: SaveWorkPointPayload = {
        code: values.code, name: values.name, address: values.address || null, city: values.city || null, isDefault: values.isDefault,
      }
      if (editing) return updateWorkPoint((workPoint as WorkPointListItem).id, payload)
      return createWorkPoint(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['administrare', 'puncte-lucru'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare punct de lucru' : 'Punct de lucru nou'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="code" control={control} render={({ field }) => (
              <TextField {...field} label="Cod" fullWidth error={!!errors.code} helperText={errors.code?.message} />
            )} />
            <Controller name="name" control={control} render={({ field }) => (
              <TextField {...field} label="Denumire" fullWidth error={!!errors.name} helperText={errors.name?.message} />
            )} />
            <Controller name="address" control={control} render={({ field }) => (
              <TextField {...field} label="Adresă (opțional)" fullWidth />
            )} />
            <Controller name="city" control={control} render={({ field }) => (
              <TextField {...field} label="Localitate (opțional)" fullWidth />
            )} />
            <Controller name="isDefault" control={control} render={({ field }) => (
              <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label="Implicit la autentificare" />
            )} />
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
